import {useEffect, useRef} from 'react'

const focusableSelector = 'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'

/** Keep keyboard interaction in the topmost dialog and return to its opener. */
export const useDialogFocus = (open: boolean, closeEnabled: boolean, onClose: () => void, suspended = false, returnFocusId?: string) => {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open || !ref.current) return
        const opener = document.activeElement as HTMLElement | null
        const openerId = returnFocusId || opener?.id
        const dialog = ref.current
        dialog.focus({preventScroll: true})
        return () => {
            if (opener?.isConnected && opener !== document.body) opener.focus({preventScroll: true})
            else if (openerId) {
                // Fullscreen details can unmount the table toolbar while open.
                requestAnimationFrame(() => {
                    document.getElementById(openerId)?.focus({preventScroll: true})
                })
            }
        }
    }, [open, returnFocusId])

    useEffect(() => {
        if (!open || suspended) return
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented || !ref.current) return
            // Inline dialogs are nested in the parent's DOM rather than a portal.
            // Let the visible child own Escape and Tab until it is dismissed.
            if (Array.from(ref.current.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]'))
                .some((dialog) => dialog.getClientRects().length > 0)) return
            if (event.key === 'Escape' && closeEnabled) {
                event.preventDefault()
                event.stopPropagation()
                onClose()
                return
            }
            if (event.key !== 'Tab') return
            const dialog = ref.current
            const controls: HTMLElement[] = []
            const visited = new Set<HTMLElement>()
            const portalOwners = new Map<HTMLElement, HTMLElement>()
            const collect = (root: HTMLElement) => {
                for (const element of [root, ...root.querySelectorAll<HTMLElement>(`${focusableSelector}, [aria-controls], [aria-owns]`)]) {
                    if (visited.has(element)) continue
                    visited.add(element)
                    if (element.matches(':disabled') || !element.getClientRects().length || element.closest('[inert], [aria-hidden="true"]')) continue
                    if (element.matches(focusableSelector) && element.tabIndex >= 0) controls.push(element)
                    // Place portalled widgets immediately after their trigger in the focus order.
                    const ids = `${element.getAttribute('aria-controls') ?? ''} ${element.getAttribute('aria-owns') ?? ''}`
                    for (const id of ids.trim().split(/\s+/)) {
                        const portal = document.getElementById(id)
                        if (portal && !dialog.contains(portal) && !visited.has(portal)) {
                            portalOwners.set(portal, element)
                            collect(portal)
                        }
                    }
                }
            }
            collect(dialog)
            const active = document.activeElement as HTMLElement | null
            let index = active ? controls.indexOf(active) : -1
            // Listboxes commonly focus a portal root with tabindex=-1.
            if (index === -1 && active) {
                const owner = Array.from(portalOwners).find(([portal]) => portal.contains(active))?.[1]
                if (owner) {
                    if (event.shiftKey) {
                        event.preventDefault()
                        owner.focus({preventScroll: true})
                        return
                    }
                    index = controls.indexOf(owner)
                }
            }
            if (!controls.length) {
                event.preventDefault()
                dialog.focus({preventScroll: true})
            } else {
                const next = index === -1
                    ? (event.shiftKey ? controls.length - 1 : 0)
                    : (index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length
                const crossesPortal = !dialog.contains(active) || !dialog.contains(controls[next])
                const wraps = index === -1 || (event.shiftKey ? index === 0 : index === controls.length - 1)
                // Leave ordinary Tab handling (including native radio groups) to the browser.
                if (crossesPortal || wraps) {
                    event.preventDefault()
                    controls[next].focus({preventScroll: true})
                }
            }
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [open, closeEnabled, onClose, suspended])

    return ref
}
